import React, { useState, useEffect } from 'react';
import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import Button from '@/components/CustomButtons/Button';
import FieldErrorText, {
  fieldErrorHelperText,
} from '@/components/FieldErrorText/FieldErrorText';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import { FacilitiesApi } from '@/api/clients/facilities/facilities.api';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Typography,
} from '@mui/material';

const labelStyle = {
  fontSize: '12px',
  color: 'grey',
  cursor: 'pointer',
};

export default function FileConfiguration({
  formValues,
  setFormValues,
  setUpdatingBlocks,
  updatingBlocks,
  editId,
  isUpdating,
  onFileConfigurationCancel,
  updateFileConfiguration,
  handleInputChange,
  fieldErrors = {},
  getFieldErrors = () => [],
  clearFieldError = () => {},
  generalErrors = [],
}) {
  const [singleFacility, setSingleFacility] = useState(
    !!formValues.facility_id,
  );
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    const fetchFacilities = async () => {
      if (singleFacility) {
        try {
          const facilitiesData = await FacilitiesApi.getFacilitiesForForms();
          setFacilities(facilitiesData || []);
        } catch (error) {
          console.error('Error fetching facilities:', error);
          setFacilities([]);
        }
      }
    };

    fetchFacilities();
  }, [singleFacility]);

  // INFO: A facility_id error always arrives with an empty value, so without
  // the error check the Select unmounts and the message has nowhere to render.
  useEffect(() => {
    setSingleFacility(!!formValues.facility_id || !!fieldErrors.facility_id);
  }, [formValues.facility_id, fieldErrors.facility_id]);

  const handleSingleFacilityChange = (event) => {
    setSingleFacility(event.target.checked);
    if (!event.target.checked) {
      clearFieldError('facility_id');
      setFormValues((prev) => ({
        ...prev,
        facility_id: '',
      }));
    }
  };

  const handleFacilitySelectChange = (event) => {
    clearFieldError('facility_id');
    setFormValues((prev) => ({
      ...prev,
      facility_id: event.target.value,
    }));
  };

  return (
    <Card className='dashboardCard table_card_height'>
      <CardBody
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}>
        <h3 className='subHeading'>File Configuration</h3>
        <FormErrorSummary errors={generalErrors} />
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <CustomInput
              type='text'
              labelText='Data Mapping Name'
              onChange={handleInputChange}
              value={formValues.name}
              name='name'
              disabled={!isUpdating('file-configuration')}
              formControlProps={{ fullWidth: true }}
              error={!!fieldErrors.name}
              inputProps={{
                helperText: fieldErrorHelperText(getFieldErrors('name')),
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <div className='custom-select'>
              <FormControl variant='standard' error={!!fieldErrors.delimiter}>
                <InputLabel id='demo-simple-select-label'>
                  File Delimiter
                </InputLabel>
                <Select
                  disabled={!isUpdating('file-configuration')}
                  labelId='demo-simple-select-label'
                  id='demo-simple-select'
                  name='delimiter'
                  value={formValues.delimiter}
                  onChange={handleInputChange}
                  className='full-width-elem'>
                  <MenuItem value={'comma'}>Comma</MenuItem>
                  <MenuItem value={'pipe'}>Pipe</MenuItem>
                  <MenuItem value={'tab'}>Tab</MenuItem>
                </Select>
                <FieldErrorText messages={getFieldErrors('delimiter')} />
              </FormControl>
            </div>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                minHeight: '100%',
              }}>
              <FormControl error={!!fieldErrors.contains_header}>
                <FormLabel
                  style={labelStyle}
                  id='demo-row-radio-buttons-group-label'>
                  Does the file contain a header with column names
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby='demo-row-radio-buttons-group-label'
                  value={formValues.contains_header}
                  onChange={handleInputChange}
                  name='contains_header'>
                  <FormControlLabel
                    value='true'
                    control={<Radio />}
                    label='Yes'
                    disabled={!isUpdating('file-configuration')}
                  />
                  <FormControlLabel
                    value='false'
                    control={<Radio />}
                    label='No'
                    disabled={!isUpdating('file-configuration')}
                  />
                </RadioGroup>
                <FieldErrorText messages={getFieldErrors('contains_header')} />
              </FormControl>
            </div>
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <CustomInput
              labelText='How many rows need to be bypassed from the top of the file'
              onChange={handleInputChange}
              type='number'
              value={formValues.rows_to_skip}
              disabled={!isUpdating('file-configuration')}
              name='rows_to_skip'
              formControlProps={{ fullWidth: true }}
              error={!!fieldErrors.rows_to_skip}
              inputProps={{
                helperText: fieldErrorHelperText(
                  getFieldErrors('rows_to_skip'),
                ),
              }}
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <div
              style={{
                border: '1px dashed #ccc',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '16px',
                marginTop: '10px',
                backgroundColor: !isUpdating('file-configuration')
                  ? '#f5f5f5'
                  : 'transparent',
              }}>
              <FormControl component='fieldset'>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={singleFacility}
                      onChange={handleSingleFacilityChange}
                      name='singleFacility'
                      disabled={!isUpdating('file-configuration')}
                    />
                  }
                  label={
                    <>
                      <Typography
                        variant='body1'
                        style={{ fontWeight: 'bold' }}>
                        Single Facility
                      </Typography>
                      <Typography variant='body2' style={{ color: '#666' }}>
                        Select a specific facility for this data mapping
                      </Typography>
                    </>
                  }
                />
              </FormControl>
            </div>
          </GridItem>
        </GridContainer>

        {singleFacility && (
          <GridContainer>
            <GridItem xs={12} sm={12} md={6}>
              <div className='custom-select'>
                <FormControl
                  variant='standard'
                  style={{ minWidth: '100%' }}
                  error={!!fieldErrors.facility_id}>
                  <InputLabel id='facility-select-label'>
                    Select Facility
                  </InputLabel>
                  <Select
                    labelId='facility-select-label'
                    value={formValues.facility_id || ''}
                    onChange={handleFacilitySelectChange}
                    disabled={!isUpdating('file-configuration')}
                    className='full-width-elem'>
                    {facilities.map((facility) => (
                      <MenuItem key={facility.id} value={facility.id}>
                        {facility.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FieldErrorText messages={getFieldErrors('facility_id')} />
                </FormControl>
              </div>
            </GridItem>
          </GridContainer>
        )}

        {editId && !updatingBlocks.includes('file-configuration') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={() =>
                setUpdatingBlocks([...updatingBlocks, 'file-configuration'])
              }
              color='primary'>
              Edit
            </Button>
          </div>
        )}
        {updatingBlocks.includes('file-configuration') && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '20px',
            }}>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={onFileConfigurationCancel}
              color='danger'>
              Cancel
            </Button>
            <Button
              style={{ marginTop: '10px', marginBottom: '-10px' }}
              onClick={updateFileConfiguration}
              color='primary'>
              Save File Configuration
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
